/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.dashboard

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Card
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.stringResource
import studio.oddpods.cauce.core.model.session.PaymentStatus
import studio.oddpods.cauce.core.model.session.SessionType
import studio.oddpods.cauce.core.ui.SessionCardContent
import studio.oddpods.cauce.feature.dashboard.generated.resources.Res
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_pending_confirm
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_pending_no_ocurrio
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_pending_title
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_session_item
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_session_status_scheduled
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_session_type_double
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_session_type_final
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_session_type_first
import studio.oddpods.cauce.feature.dashboard.generated.resources.dashboard_session_type_regular

@Composable
fun PendingConfirmationsSection(
    sessions: List<PendingConfirmationDisplay>,
    savingSessionId: String?,
    onConfirm: (String) -> Unit,
    onCancel: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    if (sessions.isEmpty()) return

    Column(modifier = modifier) {
        Text(
            text = stringResource(Res.string.dashboard_pending_title),
            style = MaterialTheme.typography.titleMedium,
            modifier = Modifier.padding(bottom = 8.dp),
        )
        sessions.forEach { display ->
            PendingConfirmationCard(
                display = display,
                isSaving = savingSessionId == display.sessionId,
                onConfirm = { onConfirm(display.sessionId) },
                onCancel = { onCancel(display.sessionId) },
            )
            Spacer(modifier = Modifier.height(8.dp))
        }
    }
}

@Composable
private fun PendingConfirmationCard(
    display: PendingConfirmationDisplay,
    isSaving: Boolean,
    onConfirm: () -> Unit,
    onCancel: () -> Unit,
    modifier: Modifier = Modifier,
) {
    var expanded by remember { mutableStateOf(false) }

    val typeLabel = stringResource(
        when (display.sessionType) {
            SessionType.REGULAR -> Res.string.dashboard_session_type_regular
            SessionType.FIRST -> Res.string.dashboard_session_type_first
            SessionType.FINAL -> Res.string.dashboard_session_type_final
            SessionType.DOUBLE -> Res.string.dashboard_session_type_double
        },
    )
    val detailText = stringResource(
        Res.string.dashboard_session_item,
        typeLabel,
        display.feeDisplay,
    )
    val statusColor = when (display.paymentStatus) {
        PaymentStatus.PAID -> MaterialTheme.colorScheme.primary
        PaymentStatus.PENDING -> MaterialTheme.colorScheme.tertiary
        PaymentStatus.PARTIAL -> MaterialTheme.colorScheme.tertiary
        PaymentStatus.WAIVED -> MaterialTheme.colorScheme.onSurfaceVariant
    }

    Card(
        onClick = { expanded = !expanded },
        modifier = modifier.fillMaxWidth(),
    ) {
        Column {
            SessionCardContent(
                patientName = display.patientName,
                sessionDate = display.sessionDate,
                detailText = detailText,
                statusLabel = stringResource(Res.string.dashboard_session_status_scheduled),
                statusColor = statusColor,
            )
            AnimatedVisibility(
                visible = expanded,
                enter = expandVertically() + fadeIn(),
                exit = shrinkVertically() + fadeOut(),
            ) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(start = 12.dp, end = 12.dp, bottom = 12.dp),
                ) {
                    OutlinedButton(
                        onClick = onCancel,
                        enabled = !isSaving,
                        modifier = Modifier.weight(1f),
                    ) {
                        Text(stringResource(Res.string.dashboard_pending_no_ocurrio))
                    }
                    TextButton(
                        onClick = onConfirm,
                        enabled = !isSaving,
                        modifier = Modifier.weight(1f),
                    ) {
                        Text(stringResource(Res.string.dashboard_pending_confirm))
                    }
                }
            }
        }
    }
}
